// Basic
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Lang
import { nsBeneficiaries } from '@/app/i18n/namespaces';
import actionsBeneficiaries from '../api/actionsBeneficiaries';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { useToast } from '@/common-utils/hooks/use-toast';
import BENEFICIARY_COLORS from '../enums/BeneficiaryColors';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.merchantAccountId
 * @param root0.beneficiaries
 * @param root0.setLaunchRefetch
 * @returns {JSX} ModalCreateBeneficiary
 */
function ModalCreateBeneficiary({ isOpen, setIsOpen, merchantAccountId, beneficiaries, setLaunchRefetch }) {

    // Toast
    const { toast } = useToast();

    // Form
    const formSchema = z.object({
        label: z.string()
            .nonempty()
            .min(1, '') // At least 1 character
            .max(100, '') // Max 100 characters
    });
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { label: '' }
    });

    // API Calls
    const onSuccess = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.create.success' namespace={nsBeneficiaries} />,
            variant: 'success'
        });
        setLaunchRefetch(true);
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.create.error' namespace={nsBeneficiaries} />,
            variant: 'danger'
        });
    };
    const submitCreateBeneficiary = actionsBeneficiaries.createBeneficiary(onSuccess, onError);

    // Functions
    const getRandomAvailableColor = () => {
        const colors = BENEFICIARY_COLORS;

        // If no beneficiaries exist, use date-based index to get a color
        if (!beneficiaries || beneficiaries.length === 0) {
            const index = Date.now() % colors.length;
            return colors[index];
        }

        // Get colors already used by existing beneficiaries
        const usedColors = new Set(
            beneficiaries
                .map(beneficiary => beneficiary.color)
                .filter(color => color) // Remove any undefined/null colors
        );

        // Filter out used colors to get available ones
        const availableColors = colors.filter(color => !usedColors.has(color));

        // If all colors are used, fall back to date-based index to get a color
        const colorsToChooseFrom = availableColors.length > 0 ? availableColors : colors;

        const index = Date.now() % colorsToChooseFrom.length;
        return colorsToChooseFrom[index];
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-lg'>
                <HeaderTitle title='modal.create.title' namespace={nsBeneficiaries} />
                <ModalBody>
                    <Form {...form}>
                        <FormLabel htmlFor='label' className='block text-sm font-medium text-gray-700 mb-2'>
                            <Entity entity='modal.create.label' namespace={nsBeneficiaries} />
                        </FormLabel>
                        <FormField
                            name='label'
                            control={form.control}
                            render={({ field }) => (
                                <FormItem className='addresses-list-input'>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            autoFocus
                                            type='text'
                                            entity='modal.create.placeholder'
                                            namespace={nsBeneficiaries}
                                            autoComplete='off'
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </Form>
                </ModalBody>
                <FooterForm
                    onSubmit={() => submitCreateBeneficiary.mutate({
                        contractAccessId: merchantAccountId,
                        color: getRandomAvailableColor(),
                        name: form.getValues().label.trim()
                    })}
                    isSubmitting={submitCreateBeneficiary.isLoading}
                    isSubmitDisabled={!form.formState.isValid}
                />
            </ModalContent>
        </Modal>
    );
}

ModalCreateBeneficiary.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    merchantAccountId: PropTypes.string.isRequired,
    beneficiaries: PropTypes.array.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default ModalCreateBeneficiary;
